import React, { useCallback, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../../../../styles/dashboardTheme.css';
import '../../../../styles/managementTheme.css';
import '../styles/malla.css';

import MallaSummary from '../components/MallaSummary';
import MallaList from '../components/MallaList';
import MallaModal from '../components/MallaModal';
import CoordinacionTabs from '../../catalogo/components/CoordinacionTabs';
import useMallaLogic from '../hooks/useMallaLogic';
import { usePermissions } from '../../../../utils/permissions';
import httpClient from '../../../../services/httpClient';

export default function MallaView() {
  const { hasPermission } = usePermissions();
  const canCreateMalla = hasPermission('malla.crear');
  const canUpdateMalla = hasPermission('malla.actualizar');
  const canDeleteMalla = hasPermission('malla.eliminar');

  const [cursos, setCursos] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(false);

  const {
    malla,
    loading,
    listError,
    lastUpdated,
    selectedCursoId,
    selectedCursoNombre,
    resumen,
    modal,
    mallaSeleccionada,
    formNuevo,
    setFormNuevo,
    savingNuevo,
    savingCambios,
    deleting,
    handleCursoChange,
    fetchMalla,
    abrirModalNuevo,
    abrirModalEliminar,
    cerrarModal,
    handleAgregarMateria,
    guardarCambiosMalla,
    handleEliminarMateria,
  } = useMallaLogic();

  const fetchCursos = useCallback(async () => {
    setCursosLoading(true);
    try {
      const response = await httpClient.get('/api/cursos');
      setCursos(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error al obtener cursos:', error);
    } finally {
      setCursosLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCursos();
  }, [fetchCursos]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (modal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [modal]);

  const lastSync = lastUpdated ? lastUpdated.toLocaleString() : 'Sincronización inicial';

  return (
    <section className="management-page management-theme-violet malla-wrapper">
      <CoordinacionTabs />
      
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Académico</p>
          <h4 className="management-title mb-1">Malla Curricular</h4>
          <p className="text-muted mb-0">
            Administra la asignación de materias a cursos y docentes.
          </p>
        </div>
        <div className="d-flex flex-wrap align-items-center justify-content-end gap-3 malla-header-actions">
          <select
            className="form-select malla-curso-select"
            value={selectedCursoId || ''}
            onChange={(e) => {
              const cursoId = parseInt(e.target.value, 10) || null;
              const cursoNombre = cursos.find((curso) => curso.id === cursoId)?.nombre || null;
              handleCursoChange(cursoId, cursoNombre);
            }}
            disabled={loading || cursosLoading}
          >
            <option value="">
              {cursosLoading ? 'Cargando cursos...' : '-- Selecciona un curso --'}
            </option>
            {cursos.map((curso) => (
              <option key={curso.id} value={curso.id}>
                {curso.nombre} ({curso.codigo_curso})
              </option>
            ))}
          </select>
          {(!selectedCursoId || !canCreateMalla) && (
            <small className="text-muted malla-sync-text">Actualizado: {lastSync}</small>
          )}
        </div>
      </div>

      {selectedCursoId && (
        <>
          <MallaSummary resumen={resumen} cursoSeleccionado={selectedCursoNombre} />

          {listError && (
            <div className="management-alert d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div>
                <p className="mb-0">{listError}</p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => fetchMalla(selectedCursoId)}
              >
                Reintentar
              </button>
            </div>
          )}

          <MallaList
            malla={malla}
            loading={loading}
            canCreateMalla={canCreateMalla}
            canUpdateMalla={canUpdateMalla}
            canDeleteMalla={canDeleteMalla}
            savingCambios={savingCambios}
            onAgregar={abrirModalNuevo}
            onEliminar={abrirModalEliminar}
            onGuardarCambios={guardarCambiosMalla}
            onRefresh={() => fetchMalla(selectedCursoId)}
          />
        </>
      )}

      {!selectedCursoId && (
        <div className="management-empty-state">
          <div className="empty-icon">
            <i className="bi bi-diagram-3"></i>
          </div>
          <p className="empty-title">Selecciona un curso</p>
          <p className="empty-description">Elige un curso arriba para ver y administrar su malla curricular</p>
        </div>
      )}

      <MallaModal
        modal={modal}
        mallaSeleccionada={mallaSeleccionada}
        formNuevo={formNuevo}
        setFormNuevo={setFormNuevo}
        materiasRegistradas={malla}
        savingNuevo={savingNuevo}
        deleting={deleting}
        onAgregar={handleAgregarMateria}
        onEliminar={handleEliminarMateria}
        onClose={cerrarModal}
      />
    </section>
  );
}
